import { Link } from 'react-router-dom'
import { Edit, Eye, MoreHorizontal, Trash2, Wrench } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { WorkerSkill } from '@/services/workers'
import { getWorkerInitials } from '@/lib/worker-list-display'
import { cn } from '@/lib/utils'

export type WorkerSkillGroupItem = {
  skill: WorkerSkill
  roleLabel: string
  categoryLabel: string
  roleCategoryLabel: string
  payLabel: string
}

type WorkerSkillsGroupCardProps = {
  workerName: string
  workerAvatar?: string | null
  workerHref?: string | null
  skills: WorkerSkillGroupItem[]
  onView: (skill: WorkerSkill) => void
  onEdit: (skill: WorkerSkill) => void
  onDelete: (skill: WorkerSkill) => void
}

const compactBadgeClass = 'h-4 px-1.5 py-0 text-[9px] font-medium leading-none'

export function WorkerSkillsGroupCard({
  workerName,
  workerAvatar,
  workerHref,
  skills,
  onView,
  onEdit,
  onDelete,
}: WorkerSkillsGroupCardProps) {
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-xl border border-border/80 bg-card/95 shadow-sm transition duration-150 hover:border-border hover:shadow-md">
      <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-indigo-400 to-violet-500" />

      <div className="border-b border-border/50 bg-muted/15 px-2.5 py-2">
        <div className="flex items-center gap-2.5">
          <Avatar className="h-11 w-11 shrink-0 border border-border/60 shadow-sm">
            <AvatarImage src={workerAvatar || undefined} alt={workerName} />
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {getWorkerInitials(workerName)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            {workerHref ? (
              <Link to={workerHref} className="line-clamp-1 text-[13px] font-semibold text-foreground hover:text-primary hover:underline">
                {workerName}
              </Link>
            ) : (
              <p className="line-clamp-1 text-[13px] font-semibold text-foreground">{workerName}</p>
            )}
            <p className="text-[10px] text-muted-foreground">
              {skills.length} active skill{skills.length === 1 ? '' : 's'}
            </p>
          </div>

          <Badge variant="outline" className={cn(compactBadgeClass, 'border-sky-200 bg-sky-50 text-sky-700')}>
            {skills.length}
          </Badge>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col divide-y divide-border/50">
        {skills.map((item) => (
            <div key={String(item.skill.id)} className="group/skill flex items-start gap-2 p-2.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-muted-foreground">
                <Wrench className="h-3.5 w-3.5" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-1">
                  {item.skill.is_primary ? (
                    <Badge variant="outline" className={cn(compactBadgeClass, 'border-amber-200 bg-amber-50 text-amber-700')}>
                      Primary
                    </Badge>
                  ) : null}
                </div>
                <p className="line-clamp-2 text-[12px] font-semibold leading-snug text-foreground">
                  {item.roleCategoryLabel}
                </p>
                <p className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">
                  {item.payLabel}
                </p>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 opacity-60 transition group-hover/skill:opacity-100"
                    aria-label={`Actions for ${item.roleLabel}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40" onClick={(event) => event.stopPropagation()}>
                  <DropdownMenuItem onClick={() => onView(item.skill)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onEdit(item.skill)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(item.skill)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
        ))}
      </div>
    </article>
  )
}

export function WorkerSkillsGroupCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-card/90">
      <div className="h-1 bg-muted" />
      <div className="flex gap-2.5 border-b border-border/50 p-2.5">
        <div className="h-11 w-11 shrink-0 rounded-full bg-muted" />
        <div className="flex-1 space-y-1.5">
          <div className="h-4 w-32 rounded bg-muted" />
          <div className="h-3 w-20 rounded bg-muted/70" />
        </div>
      </div>
      <div className="space-y-2 p-2.5">
        <div className="h-12 rounded-lg bg-muted/50" />
        <div className="h-12 rounded-lg bg-muted/40" />
      </div>
    </div>
  )
}
